import { untrack } from "svelte";
import { AppError } from "../api/problem";
import type { BuildersAddon } from "../api/contract";
import { getBuildersAddon } from "../api/endpoints";
import { isAddonOnSale, shouldShowBuildersAddonNotice } from "../billing/builders-addon";
import { prefs } from "./prefs.svelte";

/**
 * The organization's Builders add-on state, shared by the shell (upgrade-banner
 * suppression, offer filtering) and the pages that render it (Overview notice and
 * Discord gate, Billing card, Organization users). One read per organization; pages
 * call `ensure()` so they also work when mounted on their own.
 *
 * The endpoint fails closed: a `404` (the add-on switched off) or `403` marks it
 * `unavailable` and every surface renders nothing add-on related. Any other failure is
 * kept as `error` beside the last good state, so a transient 503 never hides a purchase;
 * the next `ensure()` (a page mounting, a navigation) retries it once
 * `RETRY_AFTER_ERROR_MS` has passed.
 */
export const RETRY_AFTER_ERROR_MS = 5_000;

class BuildersAddonStore {
  currentOrgId = $state<string | null>(null);
  data = $state<BuildersAddon | null>(null);
  /** The server says there is no add-on here (flag off, or not readable by this viewer). */
  unavailable = $state(false);
  loading = $state(false);
  error = $state<AppError | null>(null);

  #controller: AbortController | null = null;
  /** When the last read failed (`error`), so `ensure()` can retry without hammering. */
  #failedAt = 0;

  get active(): boolean {
    return this.data?.active === true;
  }

  /** The add-on is on sale here, so Builders is not also offered as a plan upgrade. */
  get onSale(): boolean {
    return !this.unavailable && isAddonOnSale(this.data);
  }

  /** The Overview notice for the current organization, dismissal included. */
  get noticeVisible(): boolean {
    const orgId = this.currentOrgId;
    if (!orgId || this.unavailable) return false;
    return shouldShowBuildersAddonNotice(this.data, prefs.isBuildersAddonNoticeDismissed(orgId));
  }

  setOrg(orgId: string | null): void {
    if (orgId === this.currentOrgId) return;
    this.#abort();
    this.currentOrgId = orgId;
    this.data = null;
    this.error = null;
    this.unavailable = false;
  }

  /**
   * Load once per organization; a page mounted inside the shell reuses the shell's read.
   * A read that failed is retried here once `RETRY_AFTER_ERROR_MS` has passed, so a
   * transient outage does not stick until a full reload.
   */
  ensure(orgId: string): void {
    untrack(() => {
      if (this.currentOrgId === orgId) {
        if (this.data || this.loading || this.unavailable) return;
        if (this.error && Date.now() - this.#failedAt < RETRY_AFTER_ERROR_MS) return;
      }
      void this.load(orgId);
    });
  }

  async load(orgId: string): Promise<void> {
    this.#abort();
    if (orgId !== this.currentOrgId) {
      this.currentOrgId = orgId;
      this.data = null;
      this.unavailable = false;
    }
    const controller = new AbortController();
    this.#controller = controller;
    this.loading = true;
    this.error = null;
    try {
      const data = await getBuildersAddon(orgId, controller.signal);
      if (controller.signal.aborted) return;
      this.data = data;
      this.unavailable = false;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (!(error instanceof AppError)) throw error;
      if (controller.signal.aborted) return;
      if (error.status === 404 || error.status === 403) {
        this.unavailable = true;
        this.data = null;
      } else {
        this.error = error;
        this.#failedAt = Date.now();
      }
    } finally {
      if (this.#controller === controller) {
        this.loading = false;
        this.#controller = null;
      }
    }
  }

  reload(): Promise<void> {
    return this.currentOrgId ? this.load(this.currentOrgId) : Promise.resolve();
  }

  /**
   * Adopt the fresh state an assignment change answered with (its `etag` guards the next
   * one). A response for another organization — a write that resolved after the viewer
   * switched orgs — is dropped, so it can never overwrite this organization's state or ETag.
   */
  set(orgId: string, data: BuildersAddon): void {
    if (orgId !== this.currentOrgId) return;
    this.#abort();
    this.data = data;
    this.unavailable = false;
    this.error = null;
  }

  #abort(): void {
    this.#controller?.abort();
    this.#controller = null;
    this.loading = false;
  }
}

export const buildersAddon = new BuildersAddonStore();
